import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    DialogContentText,
    FormControlLabel,
    Checkbox,
    Typography,
    Box
} from '@mui/material';
import apiWrapper from "../../apiConfig";
import Autocomplete from "@mui/material/Autocomplete";
import useAuthStore from "../../store/authStore";

const DomainDialog = ({
    open,
    isEditMode,
    domainData,
    servers,
    cloudflareAccounts,
    onClose,
    onSubmit
}) => {
    const { token } = useAuthStore();
    const [domain, setDomain] = useState({
        domain_name: '',
        file_path: '',
        type: 'main',
        transfer_type: 'auto',
        server_id: '',
        cloudflare_account_id: '',
        project_id: '',
        registrars_id: '',
        template_id: '',
        html_file: null,
    });

    const [registrars, setRegistrars] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [deploySite, setDeploySite] = useState(false);
    const [submitState, setSubmitState] = useState(false);
    const [htmlFileName, setHtmlFileName] = useState('');

    useEffect(() => {
        if (open) {
            if (domainData) {
                setDomain(domainData);
            } else {
                // Reset template_id when opening for a new domain
                setDomain(prevState => ({
                    ...prevState,
                    template_id: '',
                    html_file: null
                }));
                setDeploySite(false);
                setHtmlFileName('');
            }
        } else {
            setHtmlFileName('');
        }

    }, [open, domainData]);

    useEffect(() => {
        if (open) {
            fetchRegistrars();
            fetchTemplates();
        }
    }, [open]);

    // Запрос списка регистраторов
    const fetchRegistrars = async () => {
        try {
            const response = await apiWrapper.get(`/api/registrars`, {
                headers: { Authorization: token },
            });
            setRegistrars(response.data.data || []);
        } catch (error) {
            console.error("Ошибка при загрузке списка регистраторов:", error);
        }
    };

    // Запрос списка шаблонов
    const fetchTemplates = async () => {
        try {
            const response = await apiWrapper.get(`/api/templates`, {
                headers: { Authorization: token },
            });
            setTemplates(response.data.data || []);
        } catch (error) {
            console.error("Ошибка при загрузке списка шаблонов:", error);
        }
    };

    const handleDomainChange = (field, value) => {
        let nextState = { ...domain, [field]: value };
        if (field === 'domain_name') {
            value = value.trim().replace(/\s+/g, '');
            if(!isEditMode) {
                nextState.file_path = `/var/www/www-root/data/www/${value}`;
            }
        }
        setDomain(nextState);
    };

    const handleHtmlFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            setHtmlFileName(file.name);
            setDomain(prevState => ({
                ...prevState,
                html_file: file
            }));
        }
    };

    const handleSubmit = () => {
        setSubmitState(true);

        // Check if we need to use FormData (for file upload)
        if (deploySite && domain.template_id && domain.html_file) {
            const formData = new FormData();

            // Add all domain properties to FormData
            Object.keys(domain).forEach(key => {
                if (key === 'html_file') {
                    formData.append('html_file', domain.html_file);
                } else if (key !== 'template_id' || deploySite) {
                    // Only include template_id if deploySite is checked
                    formData.append(key, domain[key]);
                }
            });

            onSubmit(formData, true).finally(() => {
                setSubmitState(false);
            });
        } else {
            const submissionData = { ...domain };

            // Only include template_id if deploySite is checked
            if (!deploySite) {
                delete submissionData.template_id;
            }

            // Remove html_file from JSON submission
            delete submissionData.html_file;

            onSubmit(submissionData).finally(() => {
                setSubmitState(false);
            });
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{isEditMode ? 'Редактировать домен' : 'Добавить домен'}</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    {isEditMode ? 'Отредактируйте данные домена' : 'Добавьте новый домен в проект'}
                </DialogContentText>

                <TextField
                    label="Домен"
                    margin="normal"
                    fullWidth
                    value={domain.domain_name || ''}
                    onChange={(e) => handleDomainChange('domain_name', e.target.value)}
                />

                <TextField
                    label="Путь"
                    margin="normal"
                    fullWidth
                    value={domain.file_path || ''}
                    onChange={(e) => handleDomainChange('file_path', e.target.value)}
                />

                <FormControl margin="normal" fullWidth>
                    <InputLabel>Тип</InputLabel>
                    <Select
                        value={domain.type || 'main'}
                        onChange={(e) => handleDomainChange('type', e.target.value)}
                        variant={'outlined'}
                    >
                        <MenuItem value="main">Main</MenuItem>
                        <MenuItem value="transfer">Transfer</MenuItem>
                    </Select>
                </FormControl>

                <FormControl margin="normal" fullWidth>
                    <InputLabel>Режим</InputLabel>
                    <Select
                        value={domain.transfer_type || 'auto'}
                        onChange={(e) => handleDomainChange('transfer_type', e.target.value)}
                        variant={'outlined'}
                    >
                        <MenuItem value="auto">Автоматический</MenuItem>
                        <MenuItem value="manual">Ручной</MenuItem>
                    </Select>
                </FormControl>

                <FormControl margin="normal" fullWidth>
                    <InputLabel>Сервер</InputLabel>
                    <Select
                        value={domain.server_id || ''}
                        onChange={(e) => handleDomainChange('server_id', e.target.value)}
                        variant={'outlined'}
                    >
                        {servers.map((srv) => (
                            <MenuItem key={srv.id} value={srv.id}>
                                {srv.name}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <Autocomplete
                    options={[{ id: '', name: 'Нет' }, ...cloudflareAccounts]}
                    getOptionLabel={(option) => option.name}
                    value={
                        cloudflareAccounts.find((cf) => cf.id === domain.cloudflare_account_id) || null
                    }
                    onChange={(event, newValue) => {
                        handleDomainChange('cloudflare_account_id', newValue ? newValue.id : '');
                    }}
                    onInputChange={(event, newInputValue) => {
                        if (event && event.type === 'change') {
                            handleDomainChange('cloudflare_account_id', newInputValue || '');
                        }
                    }}
                    onBlur={() => {
                        if (!domain.cloudflare_account_id) {
                            handleDomainChange('cloudflare_account_id', '');
                        }
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label="Cloudflare Account"
                            margin="normal"
                            fullWidth
                        />
                    )}
                />

                <FormControl margin="normal" fullWidth>
                    <InputLabel>Регистратор</InputLabel>
                    <Select
                        value={domain.registrars_id || ''}
                        onChange={(e) => handleDomainChange('registrars_id', e.target.value)}
                        variant={'outlined'}
                    >
                        <MenuItem value="">Не выбрано</MenuItem>
                        {registrars.map((reg) => (
                            <MenuItem key={reg.id} value={reg.id}>
                                {reg.name} ({reg.type})
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                {!isEditMode && templates.length > 0 && (
                    <>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={deploySite}
                                    onChange={(e) => setDeploySite(e.target.checked)}
                                />
                            }
                            label="Разворачивать сайт"
                            margin="normal"
                        />

                        {deploySite && (
                            <>
                                <FormControl margin="normal" fullWidth>
                                    <InputLabel>Шаблон</InputLabel>
                                    <Select
                                        value={domain.template_id || ''}
                                        onChange={(e) => handleDomainChange('template_id', e.target.value)}
                                        variant={'outlined'}
                                    >
                                        <MenuItem value="">Не выбрано</MenuItem>
                                        {templates.map((template) => (
                                            <MenuItem key={template.id} value={template.id}>
                                                {template.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>

                                {domain.template_id && (
                                    <Box mt={2}>
                                        <Typography variant="subtitle2" gutterBottom>
                                            HTML файл для шаблона
                                        </Typography>
                                        <Typography variant="caption" display="block" gutterBottom>
                                            Загрузите HTML файл с title, description и body для подстановки в шаблон
                                        </Typography>
                                        <Typography variant="caption" display="block" sx={{ color: 'text.secondary', mt: 1, p: 1, bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                                            <strong>Памятка:</strong> В шаблонах используются следующие плейсхолдеры:<br />
                                            <code>{'{{SITE_URL}}'}</code> - заменяется на домен<br />
                                            <code>{'{{TITLE}}'}</code> - заменяется на title из HTML файла<br />
                                            <code>{'{{DESCRIPTION}}'}</code> - заменяется на meta description из HTML файла<br />
                                            <code>{'{{BODY}}'}</code> - заменяется на содержимое body из HTML файла
                                        </Typography>
                                        <Box display="flex" alignItems="center" mt={1}>
                                            <Button
                                                variant="outlined"
                                                component="label"
                                                size="small"
                                            >
                                                Выбрать файл
                                                <input
                                                    type="file"
                                                    accept=".html,.htm"
                                                    hidden
                                                    onChange={handleHtmlFileUpload}
                                                />
                                            </Button>
                                            <Typography variant="body2" sx={{ ml: 2 }}>
                                                {htmlFileName || 'Файл не выбран'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </>
                        )}
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Отмена</Button>
                <Button onClick={handleSubmit} disabled={submitState} variant="contained">
                    {isEditMode ? 'Сохранить' : 'Добавить'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DomainDialog;
